import {
  CRITERION_RESULT_LABELS,
  SEVERITY_LABELS,
  SLA_BAND_LABELS,
  SLA_BANDS,
  type CriterionResultValue,
  type Severity,
  type SlaBand,
} from '@convometry/contracts';
import { AlertTriangle, CheckCircle2, CircleDashed, Clock, HelpCircle, MinusCircle, XCircle } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatNumber, formatPercent } from '../../lib/format.js';

// ───────── SlaBandBar ─────────

const BAND_STYLE: Record<SlaBand, { bar: string; dot: string }> = {
  excellent: { bar: 'bg-success', dot: 'bg-success' },
  acceptable: { bar: 'bg-success/45', dot: 'bg-success/45' },
  slow: { bar: 'bg-warning', dot: 'bg-warning' },
  critical: { bar: 'bg-danger', dot: 'bg-danger' },
};

export interface SlaBandBarProps {
  /** Counts per SLA band (🟢 excellent · 🟡 acceptable · 🟠 slow · 🔴 critical). */
  bands: Record<SlaBand, number>;
  /** Accessible name, e.g. "Primer contacto por banda de SLA". */
  label: string;
  className?: string;
}

/** Stacked distribution of measurements per SLA band. Text legend always present (never color-only). */
export function SlaBandBar({ bands, label, className }: SlaBandBarProps) {
  const total = SLA_BANDS.reduce((s, b) => s + (bands[b] ?? 0), 0);
  if (total === 0) {
    return (
      <div className={cn('grid gap-2', className)}>
        <p className="text-helper font-medium text-text-secondary">{label}</p>
        <p className="text-helper text-text-secondary" data-testid="sla-empty">
          Sin mediciones en el periodo.
        </p>
      </div>
    );
  }
  return (
    <figure className={cn('grid gap-2', className)}>
      <figcaption className="text-helper font-medium text-text-secondary">{label}</figcaption>
      <div className="flex h-3 w-full overflow-hidden rounded-pill bg-surface-subtle" aria-hidden>
        {SLA_BANDS.map((b) =>
          bands[b] ? <div key={b} className={BAND_STYLE[b].bar} style={{ width: `${(bands[b] / total) * 100}%` }} /> : null,
        )}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-helper text-text-secondary">
        {SLA_BANDS.map((b) => (
          <li key={b} className="inline-flex items-center gap-1.5">
            <span aria-hidden className={cn('size-2.5 rounded-pill', BAND_STYLE[b].dot)} />
            <span className="font-medium text-text-primary">{SLA_BAND_LABELS[b]}</span>
            <span className="cm-tabular">
              {formatNumber(bands[b] ?? 0)} ({formatPercent((bands[b] ?? 0) / total, 0)})
            </span>
          </li>
        ))}
      </ul>
    </figure>
  );
}

// ───────── CriterionResultBadge ─────────

const RESULT_STYLE: Record<CriterionResultValue, { tone: string; icon: React.ReactNode }> = {
  passed: { tone: 'border-success/25 bg-success-bg text-success', icon: <CheckCircle2 aria-hidden /> },
  partial: { tone: 'border-warning/25 bg-warning-bg text-warning', icon: <AlertTriangle aria-hidden /> },
  failed: { tone: 'border-danger/25 bg-danger-bg text-danger', icon: <XCircle aria-hidden /> },
  not_applicable: { tone: 'border-border-subtle bg-surface-subtle text-text-secondary', icon: <MinusCircle aria-hidden /> },
  pending: { tone: 'border-info/25 bg-info-bg text-info', icon: <Clock aria-hidden /> },
  unknown: { tone: 'border-border-subtle bg-surface-subtle text-text-secondary', icon: <HelpCircle aria-hidden /> },
};

export interface CriterionResultBadgeProps {
  result: CriterionResultValue;
  severity?: Severity | null;
  className?: string;
}

/** Result of a criterion with icon + text; severity appended for partial/failed results. */
export function CriterionResultBadge({ result, severity, className }: CriterionResultBadgeProps) {
  const s = RESULT_STYLE[result];
  const showSeverity = severity && (result === 'partial' || result === 'failed');
  return (
    <span
      data-result={result}
      className={cn(
        'inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-pill border px-2.5 py-0.5 text-helper font-medium [&_svg]:size-3.5 [&_svg]:shrink-0',
        s.tone,
        className,
      )}
    >
      {s.icon}
      <span>{CRITERION_RESULT_LABELS[result]}</span>
      {showSeverity ? <span className="font-normal opacity-90">· Severidad {SEVERITY_LABELS[severity].toLowerCase()}</span> : null}
    </span>
  );
}

// ───────── CriterionList ─────────

export interface CriterionListItem {
  id: string;
  label: string;
  result: CriterionResultValue;
  severity?: Severity | null;
  explanation?: string | null;
  recommendedAction?: string | null;
  /** Secondary line (e.g. "Seguimiento 3 · 14 sep 10:20"). */
  meta?: React.ReactNode;
  /** Evidence links. */
  evidence?: React.ReactNode;
  /** Small marker next to the label (e.g. provenance "IA"). */
  tag?: React.ReactNode;
}

export interface CriterionListGroup {
  id: string;
  title?: string;
  description?: string;
  items: readonly CriterionListItem[];
}

export interface CriterionListProps {
  groups: readonly CriterionListGroup[];
  /** Shown when there are no items at all. */
  emptyText?: string;
  className?: string;
}

/** Compact list of evaluated criteria: result badge, explanation, recommended action and evidence. */
export function CriterionList({ groups, emptyText = 'Sin criterios evaluados.', className }: CriterionListProps) {
  const visible = groups.filter((g) => g.items.length > 0);
  if (visible.length === 0) {
    return (
      <p className={cn('flex items-center gap-2 text-body text-text-secondary', className)}>
        <CircleDashed aria-hidden className="size-4" />
        {emptyText}
      </p>
    );
  }
  return (
    <div className={cn('grid gap-5', className)}>
      {visible.map((g) => (
        <section key={g.id} aria-label={g.title} className="grid gap-2">
          {g.title ? (
            <div className="grid gap-0.5">
              <h3 className="text-helper font-semibold uppercase tracking-wide text-text-muted">{g.title}</h3>
              {g.description ? <p className="text-helper text-text-secondary">{g.description}</p> : null}
            </div>
          ) : null}
          <ul className="grid divide-y divide-border-subtle rounded-menu border border-border-subtle">
            {g.items.map((it) => (
              <li key={it.id} className="grid gap-1.5 px-3 py-2.5">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <span className="inline-flex min-w-0 items-center gap-1.5 text-body font-medium text-text-primary">
                    {it.label}
                    {it.tag}
                  </span>
                  <CriterionResultBadge result={it.result} severity={it.severity} />
                </div>
                {it.meta ? <div className="cm-tabular text-helper text-text-secondary">{it.meta}</div> : null}
                {it.explanation ? <p className="text-body text-text-secondary">{it.explanation}</p> : null}
                {it.recommendedAction ? (
                  <p className="text-helper text-text-primary">
                    <span className="font-medium">Acción sugerida:</span> {it.recommendedAction}
                  </p>
                ) : null}
                {it.evidence ? <div className="flex flex-wrap gap-1.5">{it.evidence}</div> : null}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

// ───────── StatList ─────────

export interface StatListItem {
  label: string;
  /** Pre-formatted value; null/undefined renders "—" (unknown, never 0). */
  value: React.ReactNode | null | undefined;
  hint?: string;
}

export interface StatListProps {
  items: readonly StatListItem[];
  className?: string;
}

/** Compact label/value pairs for supporting statistics next to a KPI (P90, slowest, counts…). */
export function StatList({ items, className }: StatListProps) {
  return (
    <dl className={cn('grid gap-x-6 gap-y-2 sm:grid-cols-2', className)}>
      {items.map((it) => (
        <div key={it.label} className="flex items-baseline justify-between gap-3 border-b border-border-subtle pb-1.5">
          <dt className="text-helper text-text-secondary" title={it.hint}>
            {it.label}
          </dt>
          <dd className="cm-tabular text-body font-medium text-text-primary">{it.value === null || it.value === undefined ? '—' : it.value}</dd>
        </div>
      ))}
    </dl>
  );
}
